import type { ItemFieldRow } from './item-field-reader';
import { createFormulaReadContext } from './formula-read-context';
import type { FormulaReadDependencies } from './formula-read-context';
import { normalizeInventoryPictureUuid, resolveInventoryTemplatePicture } from './inventory-template-pictures';
import type { InventoryTemplatePictureReaders } from './inventory-template-pictures';
import { readItemUuidSlot } from './inventory-raw-readers';
import { phpJsonTruthy } from './php-json-values';
import { phpJsonInteger } from './php-integer-arithmetic';
import { utf8Bytes } from './php-json-serialize';
import { putIntegerCompilePath, putPhpByteCompilePath, integerCompileWorkingRow, integerCompileResult, IntegerComputeSlotNonJsonError } from './integer-compute-slots';
import { appendPhpCompileArray, StatusArrayWriteError } from './status-row-construction';

const arrayLike = (value: unknown): value is ItemFieldRow => value !== null && typeof value === 'object';
const own = (row: unknown,key: string): unknown => arrayLike(row) && Object.prototype.propertyIsEnumerable.call(row,key)
    ? (row as Readonly<Record<string,unknown>>)[key] : undefined;
const slot = (row: unknown,key: string): unknown => typeof row === 'string' && key === '0'
    ? (row.length ? String.fromCharCode(utf8Bytes(row)[0]!) : undefined) : own(row,key);
const validUuid = (value: unknown): value is string => typeof value === 'string'
    && /^[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}(?:\n)?(?![\s\S])/i.test(value);
function scalarType(value: unknown): string {
    if (value == null) return 'null';
    if (typeof value === 'boolean') return value ? 'true' : 'false';
    const token = JSON.stringify(value);
    if (/^-?\d+$/.test(token)) {
        const integer = BigInt(token);
        if (integer >= -9223372036854775808n && integer <= 9223372036854775807n) return 'int';
    }
    return 'float';
}
function keyed(row: unknown,key: unknown): unknown {
    if (typeof row === 'string') {
        if (arrayLike(key)) throw new TypeError('Illegal PHP string offset');
        if (typeof key === 'string' && !/^[\t\n\r\v\f ]*[+-]?\d+[\t\n\r\v\f ]*$/.test(key)) return undefined;
        const bytes = utf8Bytes(row),offset = Number(phpJsonInteger(key));
        const byte = bytes[offset < 0 ? bytes.length + offset : offset];
        return byte === undefined ? undefined : String.fromCharCode(byte);
    }
    if (!arrayLike(row)) return undefined;
    if (arrayLike(key)) throw new TypeError('Illegal PHP array offset');
    const name = key == null ? '' : typeof key === 'number' || typeof key === 'boolean' ? String(phpJsonInteger(key)) : String(key);
    return own(row,name);
}
function path(row: unknown,keys: readonly string[]): unknown {
    let value = row;
    for (const key of keys) value = slot(value,key);
    return value;
}
const removed = ['inventory_id','inventory_created_by_id','inventory_name','inventory_description','inventory_written_in_language',
    'inventory_flavor_text','inventory_gm_notes','inventory_item_id','inventory_cost_gp','inventory_size','weight_override','item_qty',
    'equipped','enchantment_template_id','inventory_inspection_dc','lockpick_degree_start','lockpick_degree_end','possess_id',
    'inventory_presentation_pic_id','inventory_thumbnail_pic_id','inventory_unidentified','inventory_deleted','inventory_private',
    'item_type','size','cost_gp','weight'] as const;
export function finalizeInventoryRootSurface(input: ItemFieldRow): ItemFieldRow {
    if (!arrayLike(input)) throw new TypeError('Inventory final shape requires a PHP array');
    const out = integerCompileWorkingRow(input);
    for (const key of removed) delete out[key];
    return integerCompileResult(out);
}
export interface InventoryPictureDecoratorReaders extends InventoryTemplatePictureReaders {
    layerValue: (row: ItemFieldRow | null,key: string) => unknown;
    hasLayer: (row: ItemFieldRow,key: string) => boolean;
    getPicture: (id: string) => unknown;
    warning?: (kind: string,detail: string) => void;
}
/** Actual final picture block, then exact root-surface removal. Source row and
 * prefetch context are explicit inputs; resolved pointer mirrors are not used as
 * substitutes for original stored overrides. No DB/query implementation is wired.
 */
export function compileInventoryPictures(input: ItemFieldRow,sourceRow: unknown,context: unknown,
    readers: InventoryPictureDecoratorReaders): ItemFieldRow {
    if (!arrayLike(input)) throw new TypeError('Inventory pictures require a PHP array');
    const out = integerCompileWorkingRow(input);
    const required = (row: unknown,key: string): unknown => {
        if (typeof row === 'string' && key !== '0') throw new TypeError('Cannot access string offset with a column name');
        const value = slot(row,key);
        if (value === undefined) {
            if (arrayLike(row)) readers.warning?.('missing',key);
            else if (typeof row === 'string') readers.warning?.('missing-string-offset',key);
            else readers.warning?.('invalid-offset-read',scalarType(row));
        }
        return value ?? null;
    };
    const append = (keys: string[],value: unknown): void => {
        const current = path(out,keys);
        if (current != null && current !== false && !arrayLike(current)) throw new StatusArrayWriteError('Cannot append picture to a PHP scalar');
        putIntegerCompilePath(out,keys,appendPhpCompileArray(arrayLike(current)?current:[],value));
    };
    const each = (rows: unknown,fn: (row: unknown) => void): void => {
        if (!arrayLike(rows)) readers.invalidIteration?.();
        else for (const row of Object.values(rows)) fn(row);
    };
    const prefetch = own(context,'prefetched_pictures') ?? null;
    if (phpJsonTruthy(prefetch)) {
        const id = required(out,'inventory_id');
        each(keyed(own(prefetch,'by_attached_id'),id) ?? [],pic=>append(['pictures','inventory_picture'],pic));
    } else each(readers.fetchPictures({picture_deleted:0,attached_id:required(out,'inventory_id'),attached_item_type:'inventory_picture'}),
        pic=>append(['pictures','inventory_picture'],pic));

    const presentation = normalizeInventoryPictureUuid(own(sourceRow,'inventory_presentation_pic_id'));
    const thumbnail = normalizeInventoryPictureUuid(own(sourceRow,'inventory_thumbnail_pic_id'));
    const pointers = (a: unknown,b: unknown): void => { out.inventory_presentation_pic_id = a; out.inventory_thumbnail_pic_id = b; };
    const rawPointers = (): void => pointers(required(sourceRow,'inventory_presentation_pic_id'),required(sourceRow,'inventory_thumbnail_pic_id'));
    if (presentation !== null) pointers(presentation,thumbnail ?? presentation);
    else if (thumbnail !== null) pointers(thumbnail,thumbnail);
    else if (phpJsonTruthy(path(out,['pictures','inventory_picture']))) {
        const first = required(path(out,['pictures','inventory_picture']),'0');
        const pid = arrayLike(first) ? own(first,'picture_id') : null;
        const custom = normalizeInventoryPictureUuid(arrayLike(pid) ? own(pid,'0') ?? null : pid);
        if (custom !== null) pointers(custom,custom); else rawPointers();
    } else {
        const id = resolveInventoryTemplatePicture(out,readers);
        if (id !== null) pointers(id,id); else rawPointers();
    }
    putIntegerCompilePath(out,['inventory','inventory_presentation_pic_id'],out.inventory_presentation_pic_id ?? null);
    putIntegerCompilePath(out,['inventory','inventory_thumbnail_pic_id'],out.inventory_thumbnail_pic_id ?? null);
    const resolve = (id: string): ItemFieldRow | null => {
        if (prefetch !== null && !arrayLike(prefetch)) throw new TypeError('Picture resolver prefetch requires a PHP array or null');
        const hit = keyed(own(prefetch,'by_pic_id'),id);
        const value = hit != null ? hit : readers.getPicture(id);
        if (value !== null && !arrayLike(value)) throw new TypeError('Picture resolver return requires a PHP array or null');
        return value;
    };
    const primaryId = out.inventory_presentation_pic_id,thumbId = out.inventory_thumbnail_pic_id;
    if (phpJsonTruthy(primaryId) && validUuid(primaryId)) {
        const pic = resolve(primaryId);
        if (phpJsonTruthy(pic)) append(['pictures','inventory_presentation_picture'],pic);
    }
    let encodingError: IntegerComputeSlotNonJsonError | undefined;
    if (phpJsonTruthy(thumbId) && validUuid(thumbId)) {
        const prior = path(out,['pictures','inventory_presentation_picture']),first = slot(prior,'0');
        if (phpJsonTruthy(primaryId) && primaryId === thumbId && phpJsonTruthy(first)) {
            try {
                if (typeof prior === 'string') putPhpByteCompilePath(out,['pictures','inventory_thumbnail_picture','0'],utf8Bytes(prior)[0]!);
                else putIntegerCompilePath(out,['pictures','inventory_thumbnail_picture','0'],first);
            } catch (error) {
                if (!(error instanceof IntegerComputeSlotNonJsonError)) throw error;
                // Only JSON transport fails here in PHP. The remaining source
                // reads classifications, never this nested thumbnail field, and
                // must still run (including missing-query/error precedence).
                encodingError = error;
            }
        } else {
            const pic = resolve(thumbId);
            if (phpJsonTruthy(pic)) append(['pictures','inventory_thumbnail_picture'],pic);
        }
    }
    if (!readers.hasLayer(out,'inventory_picture')) {
        for (const layer of ['weapon_classification','weapon_ammo_classification','armor_classification','armor_type','lock_type']) {
            const id = readItemUuidSlot(readers.layerValue(out,layer) ?? '');
            if (id === '') continue;
            each(readers.fetchPictures({picture_deleted:0,attached_id:id}),pic=>append(['item_picture'],pic));
            break;
        }
    }
    if (encodingError) throw encodingError;
    return finalizeInventoryRootSurface(out);
}
export function createInventoryPictureDecorator(deps: FormulaReadDependencies & Pick<InventoryPictureDecoratorReaders,'fetchPictures' | 'getPicture' | 'invalidIteration' | 'warning'>):
    (row: ItemFieldRow,sourceRow: unknown,context: unknown) => ItemFieldRow {
    const context = createFormulaReadContext(deps),readers = {...deps,...context,
        hasLayer:(row: ItemFieldRow,key: string)=>context.layerValue(row,key) !== null || phpJsonTruthy(context.layerEntries(row,key))};
    return (row,sourceRow,inputs)=>compileInventoryPictures(row,sourceRow,inputs,readers);
}
